import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, IconButton, Stack, Fade, useTheme, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, Checkbox } from '@mui/material';
import useDataFetch from '../../utils/useDataFetch';
import api from '../../utils/api';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SizeBar from '../../components/gallery/SizeBar';

const TeamGallery = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [uploading, setUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [touchStartX, setTouchStartX] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedMediaIds, setSelectedMediaIds] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fileInputRef = useRef(null);
    const { data: profile } = useDataFetch({ url: '/users/profile' });
    const { data, isLoading, refetch } = useDataFetch({ url: `/gallery/team/${teamId}` });
    const media = data?.media || [];
    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/\/$/, '');

    const teamEntry = profile?.teams?.find((t) => t?.team_id?.toString() === teamId?.toString()) || null;
    const roleList = [];
    if (teamEntry) {
        if (teamEntry.rolle) roleList.push(teamEntry.rolle);
        if (teamEntry.sekundaer_rolle) roleList.push(...teamEntry.sekundaer_rolle.toString().split(',').map(s => s.trim()).filter(Boolean));
    }
    const canUpload = (roleList.includes('instateam')) || profile?.user?.isAdmin || profile?.user?.role === 'super_admin';

    const resolveMediaUrl = (path) => {
        if (!path) return '';
        return path.startsWith('http') ? path : `${apiBaseUrl}${path}`;
    };

    const handleDownloadUrl = (url, filename) => {
        if (!url) return;
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename || 'media';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    };

    const handleDownloadMedia = (item) => {
        const url = resolveMediaUrl(item.file_path);
        const filename = item.titel ? `${item.titel}` : `gallery_media_${item.id}`;
        handleDownloadUrl(url, filename);
    };

    const createPreviewData = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
            file,
            url: reader.result,
            type: file.type.startsWith('video') ? 'video' : 'image',
            name: file.name
        });
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    const handlePreviewClose = () => {
        setSelectedFiles([]);
        setPreviewIndex(0);
        setPreviewOpen(false);
    };

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        try {
            const previews = await Promise.all(Array.from(files).map(createPreviewData));
            setSelectedFiles(previews);
            setPreviewIndex(0);
            setPreviewOpen(true);
        } catch (error) {
            console.error('Preview creation failed', error);
            alert('Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.');
        } finally {
            e.target.value = '';
        }
    };

    const handleUploadConfirm = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        const formData = new FormData();
        selectedFiles.forEach((item) => formData.append('media', item.file));

        try {
            const response = await api.post(`/gallery/team/${teamId}/upload`, formData);
            const result = response.data;
            if (result.success) {
                handlePreviewClose();
                refetch();
            } else {
                alert(result.message || 'Fehler beim Upload');
            }
        } catch (error) {
            console.error('Upload Error', error);
            alert(error.response?.data?.message || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
        }
    };

    const toggleMediaSelection = (mediaId) => {
        setSelectedMediaIds(prev =>
            prev.includes(mediaId)
                ? prev.filter((id) => id !== mediaId)
                : [...prev, mediaId]
        );
    };

    const handleToggleSelectMode = () => {
        setSelectMode((prev) => {
            if (prev) {
                setSelectedMediaIds([]);
            }
            return !prev;
        });
    };

    const openMediaModal = (index) => {
        setCurrentMediaIndex(index);
        setGalleryOpen(true);
    };

    const handleMediaCardClick = (index, mediaId) => {
        if (selectMode) {
            toggleMediaSelection(mediaId);
            return;
        }

        openMediaModal(index);
    };

    const closeMediaModal = () => {
        setGalleryOpen(false);
    };

    const showPreviousMedia = () => {
        setCurrentMediaIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
    };

    const showNextMedia = () => {
        setCurrentMediaIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
    };

    const handleMediaTouchStart = (e) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleMediaTouchEnd = (e) => {
        if (touchStartX === null) return;
        const diff = e.changedTouches[0].clientX - touchStartX;
        if (diff > 60) {
            showPreviousMedia();
        } else if (diff < -60) {
            showNextMedia();
        }
        setTouchStartX(null);
    };

    if (isLoading) return <CircularProgress />;

    const selectedPreview = selectedFiles[previewIndex];
    const currentMedia = media[currentMediaIndex];

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto', mb: 12 }}>
            <Fade in timeout={800}>
                <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'primary.main', color: '#000' } }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.04em' }}>
                            Team<span style={{ color: theme.palette.primary.main }}>Galerie</span>
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            startIcon={<AddPhotoAlternateRoundedIcon />}
                            onClick={handleUploadClick}
                            disabled={!canUpload || uploading}
                            sx={{ borderRadius: "12px", px: 3, py: 1.5, fontWeight: 800, bgcolor: 'primary.main', color: '#000', '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            {uploading ? 'Lädt hoch...' : 'Bilder/Videos hochladen'}
                        </Button>
                        <Button
                            variant={selectMode ? 'outlined' : 'contained'}
                            startIcon={<DeleteRoundedIcon />}
                            onClick={handleToggleSelectMode}
                            sx={{ borderRadius: "12px", px: 3, py: 1.5, fontWeight: 800, color: selectMode ? '#fff' : '#000', bgcolor: selectMode ? 'error.main' : 'secondary.main', '&:hover': { bgcolor: selectMode ? 'error.dark' : 'secondary.dark' } }}
                        >
                            {selectMode ? 'Auswahl beenden' : 'Medien auswählen'}
                        </Button>
                        {selectMode && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteRoundedIcon />}
                                onClick={() => setDeleteConfirmOpen(true)}
                                disabled={selectedMediaIds.length === 0}
                                sx={{ borderRadius: "12px", px: 3, py: 1.5, fontWeight: 800 }}
                            >
                                Löschen ({selectedMediaIds.length})
                            </Button>
                        )}
                    </Stack>
                    {!canUpload && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Nur das Instateam darf hier etwas hochladen.
                        </Typography>
                    )}
                    <input type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm" hidden ref={fileInputRef} onChange={handleFileChange} />
                </Box>
            </Fade>

            {uploading && (
                <Box sx={{ mb: 4 }}>
                    <LinearProgress sx={{ borderRadius: "8px", height: 8, bgcolor: 'rgba(190, 242, 100, 0.2)', '& .MuiLinearProgress-bar': { borderRadius: '8px', bgcolor: 'primary.main' } }} />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.secondary' }}>Dateien werden verarbeitet...</Typography>
                </Box>
            )}

            <SizeBar media={media} />

            {media.length === 0 && !uploading && (
                <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mt: 10, fontWeight: 600 }}>
                    Noch keine Bilder oder Videos vorhanden.
                </Typography>
            )}

            <Box sx={{ columnCount: { xs: 1, sm: 2, md: 3, lg: 4 }, columnGap: '1rem' }}>
                {media.map((item, index) => (
                    <Box
                        key={item.id}
                        onClick={() => handleMediaCardClick(index, item.id)}
                        sx={{
                            mb: '1rem',
                            breakInside: 'avoid',
                            position: 'relative',
                            borderRadius: "12px",
                            overflow: 'hidden',
                            bgcolor: 'rgba(255,255,255,0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover .overlay': { opacity: 1 },
                            '&:hover img, &:hover video': { transform: 'scale(1.02)' }
                        }}
                    >
                        {selectMode && (
                            <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2, bgcolor: 'rgba(0,0,0,0.45)', borderRadius: "12px" }} onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={selectedMediaIds.includes(item.id)}
                                    onChange={() => toggleMediaSelection(item.id)}
                                    sx={{ color: '#fff', '&.Mui-checked': { color: 'primary.main' } }}
                                />
                            </Box>
                        )}
                        {!selectMode && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadMedia(item);
                                }}
                                sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                            >
                                <DownloadRoundedIcon fontSize="small" />
                            </IconButton>
                        )}
                        {item.file_type === 'video' ? (
                            <video
                                src={`${item.file_path}`}
                                style={{ width: '100%', display: 'block', transition: 'transform 0.4s ease' }}
                                muted
                                loop
                                onMouseOver={(e) => e.target.play()}
                                onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                            />
                        ) : (
                            <img
                                src={`${item.file_path}`}
                                alt={item.titel || 'Gallery'}
                                style={{ width: '100%', display: 'block', transition: 'transform 0.4s ease' }}
                                loading="lazy"
                            />
                        )}

                        <Box className="overlay" sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)', opacity: 0, transition: 'opacity 0.3s', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff' }}>
                                {item.first_name} {item.last_name}
                            </Typography>
                            <Stack direction="row" spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <FavoriteRoundedIcon sx={{ fontSize: 16, color: item.user_liked ? 'error.main' : 'rgba(255,255,255,0.8)' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff' }}>{item.like_count}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.8)' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff' }}>{item.comment_count}</Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Dialog open={previewOpen} onClose={handlePreviewClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Dateien bestätigen
                    <IconButton onClick={handlePreviewClose}>
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Bitte überprüfe die ausgewählten Dateien und bestätige den Upload.
                    </Typography>
                    {selectedPreview && (
                        <Box sx={{ width: '100%', height: 280, mb: 2, position: 'relative', borderRadius: "12px", overflow: 'hidden', bgcolor: 'background.paper', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {selectedPreview.type === 'video' ? (
                                <video
                                    src={selectedPreview.url}
                                    controls
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
                                />
                            ) : (
                                <img
                                    src={selectedPreview.url}
                                    alt={selectedPreview.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            )}
                        </Box>
                    )}
                    {selectedFiles.length > 1 && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: 'auto', pb: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                            {selectedFiles.map((preview, index) => (
                                <Box
                                    key={`${preview.name}-${index}`}
                                    onClick={() => setPreviewIndex(index)}
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: "12px",
                                        overflow: 'hidden',
                                        border: index === previewIndex ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(255,255,255,0.12)',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        position: 'relative'
                                    }}
                                >
                                    {preview.type === 'video' ? (
                                        <video src={preview.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <img src={preview.url} alt={preview.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handlePreviewClose} variant="outlined">Abbrechen</Button>
                    <Button onClick={handleUploadConfirm} variant="contained" disabled={uploading}>
                        {uploading ? 'Hochladen...' : 'Hochladen und speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Ausgewählte Medien löschen?</DialogTitle>
                <DialogContent>
                    <Typography>Willst du wirklich {selectedMediaIds.length} ausgewählte Medien löschen? Diese Aktion kann nicht rückgängig gemacht werden.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined">Abbrechen</Button>
                    <Button
                        onClick={async () => {
                            setDeleting(true);
                            try {
                                const response = await api.delete('/gallery', { data: { ids: selectedMediaIds } });
                                if (response.data.success) {
                                    setSelectedMediaIds([]);
                                    setSelectMode(false);
                                    refetch();
                                } else {
                                    alert(response.data.message || 'Löschen fehlgeschlagen');
                                }
                            } catch (error) {
                                console.error('Delete Error', error);
                                alert(error.response?.data?.message || 'Löschen fehlgeschlagen');
                            } finally {
                                setDeleting(false);
                                setDeleteConfirmOpen(false);
                            }
                        }}
                        variant="contained"
                        color="error"
                        disabled={selectedMediaIds.length === 0 || deleting}
                    >
                        {deleting ? 'Lösche...' : 'Löschen'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={galleryOpen}
                onClose={closeMediaModal}
                fullWidth
                maxWidth="xl"
                fullScreen={fullScreen}
                PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', boxShadow: 'none', minHeight: '80vh' } }}
                BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' }}>
                    <Typography variant="h6">Galerie ansehen</Typography>
                    <IconButton onClick={closeMediaModal} sx={{ color: '#fff' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ position: 'relative', py: 3, px: { xs: 1, md: 4 } }}>
                    {currentMedia && (
                        <Box
                            onTouchStart={handleMediaTouchStart}
                            onTouchEnd={handleMediaTouchEnd}
                            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, position: 'relative' }}
                        >
                            <IconButton onClick={showPreviousMedia} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.35)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}>
                                <KeyboardArrowLeftRoundedIcon fontSize="large" />
                            </IconButton>
                            <Box sx={{ width: '100%', maxWidth: 1100, maxHeight: 700, borderRadius: "12px", overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.04)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {currentMedia.file_type === 'video' ? (
                                    <video
                                        src={`${process.env.REACT_APP_API_URL || ''}${currentMedia.file_path}`}
                                        controls
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <img
                                        src={`${process.env.REACT_APP_API_URL || ''}${currentMedia.file_path}`}
                                        alt={currentMedia.titel || 'Gallery'}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                )}
                            </Box>
                            <IconButton onClick={showNextMedia} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.35)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}>
                                <KeyboardArrowRightRoundedIcon fontSize="large" />
                            </IconButton>
                        </Box>
                    )}
                    {currentMedia && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography variant="subtitle1" color="grey.300" sx={{ mb: 1 }}>
                                {currentMedia.titel || 'Unbenanntes Medium'}
                            </Typography>
                            <Typography variant="body2" color="grey.400">
                                {currentMedia.first_name} {currentMedia.last_name} • {currentMedia.comment_count} Kommentare • {currentMedia.like_count} Likes
                            </Typography>
                            <Typography variant="caption" color="grey.500" sx={{ display: 'block', mt: 1 }}>
                                {currentMediaIndex + 1} / {media.length}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TeamGallery;
